import SearchInput from "@/manager/components/search-input/search-input";
import { noop } from "@/manager/utils";
import EnumerationField from "@/shared/components/enumeration-field/enumeration-field";
import TextInput from "@/shared/components/text-input/text-input";
import { Fragment, useEffect, useState } from "react";
import { IDatasetLabel } from "./dataset-edit";
import { IEnumerationOption } from "@/shared/definitions/datatypes";
import "./dataset-label-modal.scss";

export interface IDatasetLabelModalProps {
  selectedData: IDatasetLabel;
  selectableFields: IDatasetLabel[];
  amountDirectionCandidates: IDatasetLabel[];
  onSelectedDataChanged: (data: IDatasetLabel) => void;
}

const labelOptionPerDatatype = {
  "text": [
    { name: "Entity", id: "entity" },
    { name: "Category", id: "category" },
    { name: "Amount direction", id: "amount_direction" }
  ],
  "integer": [
    { name: "Amount", id: "amount" },
    { name: "Category", id: "category" }
  ],
  "float": [
    { name: "Amount", id: "amount" },
  ],
  "datetime": [
    { name: "DateTime", id: "datetime" }
  ],
  "boolean": [
    { name: "Suspicious", id: "suspicious" },
    { name: "Alerted", id: "alert" },
    { name: "Amount direction", id: "amount_direction" }
  ]
}

const DatasetLabelModal = ({
  selectedData, 
  selectableFields, 
  amountDirectionCandidates, 
  onSelectedDataChanged
}: IDatasetLabelModalProps) => {
  const [fieldOptions, setFieldOptions] = useState<IDatasetLabel[]>(selectableFields);
  useEffect(() => {
    if (selectedData.id) {
      setFieldOptions([...selectableFields, selectedData]);
    }
  }, [selectedData.id, selectableFields]);
  
  const handleOnSearchChange = (newOption: IEnumerationOption) => {
    const option = fieldOptions.find(field => field.id === newOption.id);
    if (!option) {
      return;
    } 
    onSelectedDataChanged({
      ...option
    });
  };

  const getLabelOptions = () => {
    if (!selectedData.datamodel_field_type) {
      return [];
    }
    return labelOptionPerDatatype[selectedData.datamodel_field_type as keyof typeof labelOptionPerDatatype];
  };

  return <div className="datasetLabelModal">
    <SearchInput
      inputKey="field_name" 
      handleOnChange={handleOnSearchChange}
      label="Fieldname" 
      required={true}
      data={fieldOptions.map(field => ({ id: field.id, name: field.field_name}))}
      selectedOption={selectedData.id}
      onFocusEmpty={true}
    />
    { selectedData.id &&
     <div className="datasetLabelModal-stats">
      <p className="datasetLabelModal-statsTitle">Field information:</p>
        <p>Null Values: {selectedData.null_values || "0"}</p>
        <p>Empty Values: {selectedData.empty_values || "0"}</p>
        <p>Distinct Values: {selectedData.distinct_values || "0"}</p>
        <p>Total Rows: {selectedData.total_rows || "0"}</p>
      </div>
    }
    <TextInput 
      type="text"
      id={"datamodel_field_type"}
      inputKey="datamodel_field_type" 
      handleOnChange={noop}
      label="Fieldtype" 
      required={false}
      disabled={true}
      controlled={true}
      value={selectedData.datamodel_field_type}
    />
    <EnumerationField 
      inputKey="label" 
      handleOnChange={(value) => onSelectedDataChanged({ ...selectedData, label: String(value) })}
      label="Label" 
      required={true}
      data={getLabelOptions()}
      defaultValue={selectedData.label}
      autoFocus={false}
    />
    {selectedData.label === "amount" && <SearchInput
      inputKey="amount_direction_id" 
      handleOnChange={(newOption) => onSelectedDataChanged({ ...selectedData, amount_direction_id: newOption?.id})}
      label="Amount direction" 
      required={false}
      data={amountDirectionCandidates.map(field => ({ id: field.id, name: field.field_name}))}
      selectedOption={""}
      onFocusEmpty={true}
    />}
    {selectedData.label === "amount_direction" && <Fragment>
      <TextInput 
        type="text"
        id={"positive"}
        inputKey="positive" 
        handleOnChange={(value) => onSelectedDataChanged({ ...selectedData, positive: String(value) })}
        label="Positive value" 
        required={false}
        controlled={true}
        value={selectedData.positive}
      />
      <TextInput 
        type="text"
        id={"negative"}
        inputKey="negative" 
        handleOnChange={(value) => onSelectedDataChanged({ ...selectedData, negative: String(value) })}
        label="Negative value" 
        required={false}
        controlled={true}
        value={selectedData.negative}
      />
    </Fragment> 
    }
  </div>;
};

export default DatasetLabelModal;